<template>
    <div class="tracking-aside__header">
        <div class="tracking-aside__top">
            <div class="tracking-aside__vrn">
                VRN: {{ vehicle.registrationNumber || vehicle.vrn || 'Unknown' }}
                <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                        <div
                            class="tracking-aside__is-online"
                            :class="{'tracking-aside__online': isHardwareOnline}"
                            v-on="on"
                        ></div>
                    </template>
                    <span>{{ isHardwareOnline ? 'Device is online' : 'Device is offline' }}</span>
                </v-tooltip>
            </div>
            <div class="tracking-aside__close">
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <span v-on="on">
                            <icon v-if="isAdmin()" @click.native="handleReprocessJourneys" class="icon-reprocess" icon="reprocess" :width="19" :height="19"/>
                        </span>
                    </template>
                    <span>Request Journeys Reprocessing</span>
                </v-tooltip>

                <icon @click.native="closeJourneyTab" class="icon-close" icon="close" :width="19" :height="19"/>
            </div>
        </div>

        <div v-if="withDatePicker">
            <date-picker
                v-model="dateInJourneyTab"
                :placeholder="dateInJourneyTab.toString()"
                :clearable="false"
                :value-type="this.selectedDate"
                :disabled-date="disabledAfterToday"
                @input="dateChanged"
                type="date"
            ></date-picker>
        </div>
    </div>
</template>

<script>

import moment from 'moment'
import TrackingService from '@/services/TrackingService'
import Icon from '@/components/atoms/Icon'
import DatePicker from 'vue2-datepicker'

import { DeviceEventApi, JourneyApi, MediaRequestApi } from '@/api'
import { mapActions, mapGetters } from 'vuex'

const journeyApi = new JourneyApi()
const deviceEventApi = new DeviceEventApi()
const mediaRequestApi = new MediaRequestApi()

export default {
    name: 'AsideContentHeader',
    components: {
        Icon,
        DatePicker
    },
    props: {
        withDatePicker: {
            type: Boolean,
            default: false
        },
        vehicle: {
            type: [Object, null],
            required: true
        },
        isHardwareOnline: {
            type: [Boolean],
            required: true
        }
    },
    created () {
        if (this.$route.params.date && !this.getIsOpenActionJourneyTab) {
            this.dateInJourneyTab = moment(this.$route.params.date).format('YYYY-MM-DD')
        } else if (this.getIsOpenActionJourneyTab) {
            this.dateInJourneyTab = moment(this.getLastActiveDate).format('YYYY-MM-DD')
            this.setIsOpenActionJourneyTab(false)
        } else {
            this.dateInJourneyTab = moment(this.dateInJourneyTab, 'YYYY-MM-DD').format('YYYY-MM-DD')
        }
    },
    data () {
        return {
            isDatePickerShow: false,
            selectedDate: '',
            isDateChange: true
        }
    },
    computed: {
        ...mapGetters({
            getMenuState: 'layoutModule/getMenuState',
            currentJourney: 'trackingModule/getCurrentJourney',
            getDateInJourneyTab: 'trackingModule/getDateInJourneyTab',
            getIsOpenActionJourneyTab: 'trackingModule/getIsOpenActionJourneyTab',
            getLastActiveDate: 'trackingModule/getLastActiveDate'
        }),
        maxDate () {
            return moment().format('YYYY-MM-DD')
        },
        dateInJourneyTab: {
            get () {
                return this.$store.state.trackingModule.dateInJourneyTab
            },
            set (value) {
                this.$store.commit('trackingModule/setDateInJourneyTab', value)
            }
        }
    },
    methods: {
        ...mapActions({
            setMenuAction: 'layoutModule/setMenuAction',
            clearActiveJourneys: 'trackingModule/clearActiveJourneys',
            setCurrentJourney: 'trackingModule/setCurrentJourney',
            removeActiveJourney: 'trackingModule/removeActiveJourney',
            clearSelectedJourneys: 'trackingModule/clearSelectedJourneys',
            clearSubscriptionJourneyVehicle: 'trackingModule/clearSubscriptionJourneyVehicle',
            setIsTrackingAsideLoading: 'trackingModule/setIsTrackingAsideLoading',
            setIsJourneyTabActive: 'trackingModule/setIsJourneyTabActive',
            setOpenAutoJourney: 'trackingModule/setOpenAutoJourney',
            setLoading: 'trackingModule/setLoading',
            setIsOpenActionJourneyTab: 'trackingModule/setIsOpenActionJourneyTab'
        }),
        async dateChanged () {
            this.setIsTrackingAsideLoading(true);
            this.clearSelectedJourneys()
            const dateFrom = moment(this.dateInJourneyTab).startOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z'
            const dateTo = moment(this.dateInJourneyTab).endOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z'

            const [journeyResponse, eventsResponse, mediaResponse] = await Promise.allSettled([
                journeyApi.getJourneys(dateFrom, dateTo, this.vehicle.id, !!this.getIsOpenActionJourneyTab),
                deviceEventApi.getDeviceEvents({
                    dateFrom,
                    dateTo,
                    vehicleId: this.vehicle.id,
                    recordLimit: 10000
                }),
                mediaRequestApi.getMediaRequests({
                    videoTimeFrom: dateFrom,
                    videoTimeTo: dateTo,
                    hardwareId: this.vehicle.mediaHardwareId
                })
            ])

            //TODO: error handling to discuss
            this.removeActiveJourney(this.currentJourney)
            const journeys = TrackingService.transformJourneyRequest(journeyResponse.value, eventsResponse.value, mediaResponse.value, this.vehicle)
            this.setCurrentJourney(journeys)
            this.setIsTrackingAsideLoading(false);
            this.setIsOpenActionJourneyTab(false)
            this.$emit('dateChange', this.isDateChange)
        },
        async closeJourneyTab () {
            this.clearActiveJourneys()
            this.clearSelectedJourneys()
            this.setCurrentJourney(undefined)
            this.clearSubscriptionJourneyVehicle()
            await this.setMenuAction(!this.getMenuState)
            this.setIsJourneyTabActive(false)
            this.setOpenAutoJourney(true)
        },
        toggleDatePickerShow () {
            this.isDatePickerShow = !this.isDatePickerShow
        },
        disabledAfterToday (date) {
            const today = new Date()

            return date > today
        },
        async handleReprocessJourneys () {
            this.setIsTrackingAsideLoading(true)
            const dateFrom = moment(this.dateInJourneyTab).startOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z'
            const dateTo = moment(this.dateInJourneyTab).endOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z'
            const journeyReprocessResult = await journeyApi.reprocessJourneys(
                dateFrom,
                dateTo,
                this.vehicle.id
            )

            if (!journeyReprocessResult.isSuccess) {
                this.setIsTrackingAsideLoading(false)
                return
            }

            this.clearSelectedJourneys()

            const [journeyResponse, eventsResponse, mediaResponse] = await Promise.allSettled([
                journeyApi.getJourneys(dateFrom, dateTo, this.vehicle.id, false),
                deviceEventApi.getDeviceEvents({
                    dateFrom,
                    dateTo,
                    vehicleId: this.vehicle.id,
                    recordLimit: 10000
                }),
                mediaRequestApi.getMediaRequests({
                    videoTimeFrom: dateFrom,
                    videoTimeTo: dateTo,
                    hardwareId: this.vehicle.mediaHardwareId
                })
            ])

            this.removeActiveJourney(this.currentJourney)
            const journeys = TrackingService.transformJourneyRequest(journeyResponse.value, eventsResponse.value, mediaResponse.value, this.vehicle)
            this.setCurrentJourney(journeys)
            this.setIsTrackingAsideLoading(false)
        },
        isAdmin () {
            return this.hasAllRoles(['Admin'])
        }
    },
    watch: {
        getLastActiveDate: {
            handler (val) {
                if (val) {
                    this.dateInJourneyTab = moment(val).format('YYYY-MM-DD')
                    this.dateChanged()
                }
            },
            deep: true
        }
    }
}
</script>

<style scoped lang="scss">
.tracking-aside {
    display: flex;
    flex-direction: row;
    min-width: 450px;

    &__header {
        width: 100%;
        margin-bottom: 10px;
        padding: 10px 15px;
    }

    &__vrn {
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: bold;
    }

    &__top {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
    }

    &__close {
        display: flex;
        align-items: center;
        cursor: pointer;
    }

    &__is-online {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-left: 7px;
        background-color: $danger;
    }

    &__online {
        background-color: #77f146;
    }

    .icon-reprocess {
        margin-right: 10px;
        &:hover {
            opacity: 0.7;
        }
    }
    .icon-close {
        &:hover {
            opacity: 0.7;
        }
    }
}

::v-deep .small-field {
    .v-input__append-inner {
        align-self: center;
        margin: 0px;
    }
}
</style>
